/**
 * Düğme — **parmak** mertebesi (docs/13-TASARIM-SISTEMI.md bölüm 1).
 *
 * Yaşadığı yer: daima bir elin içinde (araç çubuğu, kart başlığı, tablo satırı).
 * Kendi dış boşluğunu YAZMAZ — aralığı onu yerleştiren kap `gap` ile verir.
 *
 * Yarıçap `--radius-sm` (6px): iç içe yarıçap formülü gereği bir kartın
 * (14px) içindeki düğme daha küçük yarıçap kullanır, yoksa köşeler iç içe
 * geçmez ve göz bunu "ucuz" okur.
 *
 * Durum rengi ALMAZ (doküman 6): vurgu rengi "tıkla" der, durum rengi
 * "şu anda şöyle" der. Tehlikeli işlem için `tehlike` görünümü ayrıdır ve
 * durum rozetiyle aynı rengi paylaşsa da anlamı farklıdır — eylemdir.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type DugmeGorunumu = 'birincil' | 'ikincil' | 'sessiz' | 'tehlike';
export type DugmeBoyu = 'sm' | 'md';

const GORUNUM: Record<DugmeGorunumu, string> = {
  // Vurgu gölgesi (Vuexy): birincil düğme zeminden hafif kalkar — sayfadaki
  // TEK birincil eylem göze ilk o çarpar. İkincil düğmelerde gölge yok.
  birincil:
    'bg-[var(--accent)] text-[var(--accent-ink)] hover:bg-[var(--accent-hover)] border border-transparent shadow-[var(--shadow-vurgu)]',
  ikincil:
    'bg-[var(--surface)] text-[var(--ink)] border border-[var(--line-strong)] hover:bg-[var(--surface-hover)]',
  sessiz:
    'bg-transparent text-[var(--ink-muted)] border border-transparent hover:bg-[var(--surface-hover)] hover:text-[var(--ink)]',
  tehlike:
    'bg-[var(--state-bad-soft)] text-[var(--state-bad)] border border-[var(--state-bad)] hover:bg-[var(--state-bad)] hover:text-[var(--surface)]',
};

/**
 * Boy yalnızca dolgu ve punto değiştirir; yükseklik içerikten gelir (doküman 4.5).
 *
 * Yatay dolgu ölçüldü (Vuexy `$btn-padding-x: 1.25rem` = 20px, sm 14px).
 * Dar düğme "tıklanmaz" görünüyor ve dokunmatikte ıskalanıyor.
 */
const BOY: Record<DugmeBoyu, string> = {
  sm: 'gap-1.5 px-3 py-1.5 text-xs',
  md: 'gap-2 px-5 py-2 text-sm',
};

export interface DugmeProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  gorunum?: DugmeGorunumu;
  boy?: DugmeBoyu;
  /** İşlem sürüyor: düğme kilitlenir ve metin yerine bekleme gösterilir. */
  bekliyor?: boolean;
  ikon?: ReactNode;
  children?: ReactNode;
}

export function Dugme({
  gorunum = 'ikincil',
  boy = 'md',
  bekliyor = false,
  ikon,
  className,
  children,
  disabled,
  ...kalan
}: DugmeProps) {
  return (
    <button
      type="button"
      // Bekleyen düğme de kilitlidir: çift tıklama iki fatura göndermesin.
      disabled={disabled === true || bekliyor}
      aria-busy={bekliyor}
      className={cn(
        'inline-flex items-center justify-center rounded-[var(--radius-sm)] font-medium',
        'transition-colors duration-150 select-none whitespace-nowrap',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        GORUNUM[gorunum],
        BOY[boy],
        className
      )}
      {...kalan}
    >
      {bekliyor ? (
        <span
          className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : (
        // İkon 1px yukarı: harflerin optik merkezi taban çizgisine yakın (doküman 5).
        ikon && (
          <span className="-mt-px inline-flex shrink-0" aria-hidden="true">
            {ikon}
          </span>
        )
      )}
      {children}
    </button>
  );
}
